import { useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../flux/stores/hooks';
import { createOrganization, renameOrganization } from '../../flux/actions/organizationThunks';
import { useApiAccessToken } from '../../utils/useApiAccessToken';
import { OrganizationMembers } from './OrganizationMembers';
import { OrganizationRules } from './OrganizationRules';
// Reuses the pd-header/pd-tabs structure ProjectDetail defined, since this
// page follows the same "header + tabs" shape.
import '../Projects/ProjectDetail/ProjectDetail/ProjectDetail.css';

const MEMBERS_TAB = { id: 'members', label: 'Manage members' };
const RULES_TAB = { id: 'rules', label: 'Active rules' };

// Org data is loaded by SideNav (always mounted alongside this page via
// PageLayout) — see useEnsureOrganizationLoaded. No need to trigger it again here.
export const Organization = () => {
    const dispatch = useAppDispatch();
    const { getAccessToken } = useApiAccessToken();

    const org = useAppSelector(state => state.organization.data);
    const error = useAppSelector(state => state.organization.error);
    const notMember = useAppSelector(state => state.organization.notMember);
    const userData = useAppSelector(state => state.user.current.data);
    const orgRole = userData?.org_role;
    const licenseTier = userData?.license_tier;
    const isAdmin = orgRole === 'admin';

    const [newOrgName, setNewOrgName] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isRenaming, setIsRenaming] = useState(false);
    const [renameValue, setRenameValue] = useState('');

    const tabs = isAdmin ? [MEMBERS_TAB, RULES_TAB] : [RULES_TAB];
    const [tab, setTab] = useState(tabs[0].id);
    // Losing admin standing mid-session must not strand the page on a tab
    // that no longer exists.
    const activeTab = tabs.some(t => t.id === tab) ? tab : tabs[0].id;

    const handleCreate = async (event) => {
        event.preventDefault();
        const name = newOrgName.trim();
        if (!name) return;

        setIsSubmitting(true);
        const accessToken = await getAccessToken();
        if (accessToken) {
            await dispatch(createOrganization({ accessToken, name }));
        }
        setIsSubmitting(false);
    };

    const startRename = () => {
        setRenameValue(org?.name ?? '');
        setIsRenaming(true);
    };

    const handleRename = async (event) => {
        event.preventDefault();
        const name = renameValue.trim();
        if (!name) return;

        setIsSubmitting(true);
        const accessToken = await getAccessToken();
        if (accessToken) {
            const result = await dispatch(renameOrganization({ accessToken, name }));
            if (renameOrganization.fulfilled.match(result)) {
                setIsRenaming(false);
            }
        }
        setIsSubmitting(false);
    };

    // Covers the gap between mount and the fetch actually dispatching (token
    // acquisition happens first) as well as the fetch being in flight — until we
    // have a definitive answer (an org, a confirmed "not a member", or an error),
    // always show the same loading state so the page doesn't flash a "blank" or
    // wrong-state render before flipping to the real content.
    if (!org && !notMember && !error) {
        return (
            <div className="page-content">
                <p className="projects-loading">Loading organization…</p>
            </div>
        );
    }

    if (notMember) {
        if (!userData) {
            return (
                <div className="page-content">
                    <p className="projects-loading">Loading organization…</p>
                </div>
            );
        }

        if (licenseTier !== 'pro') {
            return (
                <div className="page-content">
                    <div className="pw-page-header">
                        <h1 className="pw-page-title">Organization</h1>
                    </div>
                    <div className="pw-container">
                        <div className="pw-section">
                            <p className="pw-hint">
                                You're not part of an organization yet. Creating one requires a Pro
                                license — contact your account manager to upgrade. If your
                                organization already exists, ask its administrator to invite you as
                                a member.
                            </p>
                        </div>
                    </div>
                </div>
            );
        }

        return (
            <div className="page-content">
                <div className="pw-page-header">
                    <h1 className="pw-page-title">Create your organization</h1>
                </div>
                <div className="pw-container">
                    <form className="pw-section" onSubmit={handleCreate}>
                        {error && <p className="org-error">{error}</p>}
                        <label className="pw-label" htmlFor="org-name">Organization name</label>
                        <input
                            id="org-name"
                            className="pw-input"
                            placeholder="e.g. Acme Corp"
                            value={newOrgName}
                            onChange={e => setNewOrgName(e.target.value)}
                            autoFocus
                        />
                        <div className="pw-actions">
                            <button
                                type="submit"
                                className="rules-btn-primary"
                                disabled={!newOrgName.trim() || isSubmitting}
                            >
                                {isSubmitting ? 'Creating…' : 'Create organization'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        );
    }

    if (!org) {
        return (
            <div className="page-content">
                {error && <p className="org-error">{error}</p>}
            </div>
        );
    }

    return (
        <div className="page-content">
            <div className="pd-header">
                {isRenaming ? (
                    <form className="pd-rename-form" onSubmit={handleRename}>
                        <input
                            className="pw-input"
                            value={renameValue}
                            onChange={e => setRenameValue(e.target.value)}
                            disabled={isSubmitting}
                            aria-label="Organization name"
                            autoFocus
                        />
                        <button type="submit" className="rules-btn-primary" disabled={!renameValue.trim() || isSubmitting}>
                            {isSubmitting ? 'Saving…' : 'Save'}
                        </button>
                        <button type="button" className="rules-btn-ghost" onClick={() => setIsRenaming(false)} disabled={isSubmitting}>
                            Cancel
                        </button>
                    </form>
                ) : (
                    <div className="pd-header-main">
                        <h1 className="rules-title">{org.name}</h1>
                        {isAdmin && (
                            <div className="pd-header-actions">
                                <button type="button" className="rules-btn-ghost" onClick={startRename}>
                                    Rename
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {error && <p className="org-error">{error}</p>}

            <div className="pd-tabs">
                {tabs.map(t => (
                    <button
                        key={t.id}
                        type="button"
                        className={`pd-tab${activeTab === t.id ? ' active' : ''}`}
                        onClick={() => setTab(t.id)}
                    >
                        {t.label}
                    </button>
                ))}
            </div>

            {activeTab === 'members' && <OrganizationMembers />}
            {activeTab === 'rules' && <OrganizationRules />}
        </div>
    );
};

export default Organization;
